import { useEffect, useState, useCallback } from "react";
import { fmtDate } from "../lib/date";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Wrench, ArrowRight } from "@phosphor-icons/react";
import { PriorityBadge, PrioritySelect, priorityRank } from "../components/Priority";

export default function MaintenancePage() {
  const { hasPerm } = useAuth();
  const canWrite = hasPerm("maintenance", "supervisor", "administrator");
  const [jobs, setJobs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [open, setOpen] = useState(false);
  const [prFilter, setPrFilter] = useState("");
  const [form, setForm] = useState({ asset_id: "", maintenance_type_id: "", schedule_id: "", title: "", priority: "medium", due_date: "" });

  const load = useCallback(() => { api.get("/maintenance/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
    if (canWrite) {
      api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
      api.get("/config/maintenance-types").then((r) => setTypes(r.data)).catch(() => {});
    }
  }, [load, canWrite]);

  useEffect(() => {
    if (form.asset_id) api.get(`/maintenance/schedules?asset_id=${form.asset_id}`).then((r) => setSchedules(r.data)).catch(() => {});
    else setSchedules([]);
  }, [form.asset_id]);

  const create = async () => {
    try {
      const r = await api.post("/maintenance/jobs", form);
      toast.success("Job created"); setOpen(false);
      setForm({ asset_id: "", maintenance_type_id: "", schedule_id: "", title: "", priority: "medium", due_date: "" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const assetName = (id) => { const a = assets.find((x) => x.id === id); return a ? `${a.asset_number} · ${a.name}` : id; };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Maintenance</h1>
        {canWrite && <Button data-testid="new-job" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 font-semibold"><Plus size={18} weight="bold" className="mr-1" />New Job</Button>}
      </div>

      <div className="flex gap-2 items-center">
        <label className="text-sm font-semibold text-slate-600">Priority</label>
        <select data-testid="job-filter-priority" value={prFilter} onChange={(e) => setPrFilter(e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
          <option value="">All</option><option value="urgent">Urgent</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
      </div>

      <div className="flex flex-col gap-2">
        {jobs.filter((j) => !prFilter || j.priority === prFilter).sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority)).map((j) => (
          <Link key={j.id} to={`/maintenance/jobs/${j.id}`} data-testid={`job-row-${j.id}`}
            className="bg-white border-2 border-slate-200 rounded-lg p-4 flex items-center justify-between hover:border-blue-500 transition-colors duration-100 border-l-8"
            style={{ borderLeftColor: j.status === "completed" ? "#10b981" : j.status === "awaiting_parts" ? "#f59e0b" : "#2563eb" }}>
            <div>
              <p className="font-body font-semibold text-slate-800 flex items-center gap-2"><Wrench size={16} weight="bold" className="text-slate-400" />{j.title || "Maintenance job"} {j.status !== "completed" && <PriorityBadge priority={j.priority} testid={`job-priority-${j.id}`} />}{j.status === "completed" && <span data-testid={`job-completed-${j.id}`} className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Completed</span>}</p>
              <p className="font-mono text-xs text-slate-500 mt-1">{assetName(j.asset_id) || j.asset_number || ""} · {j.status}{j.due_date ? ` · due ${fmtDate(j.due_date)}` : ""}{j.assignee_name ? ` · ${j.assignee_name}` : ""}{j.cost ? ` · £${j.cost}` : ""}</p>
            </div>
            <ArrowRight size={18} className="text-slate-400" />
          </Link>
        ))}
        {jobs.length === 0 && <p className="text-slate-400">No maintenance jobs yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Maintenance Job</DialogTitle></DialogHeader>
          <Input data-testid="job-title" placeholder="Title (e.g. 250h service)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div><label className="text-xs font-semibold text-slate-600">Priority</label><PrioritySelect testid="job-priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} className="w-full" /></div>
          <select data-testid="job-asset" className="h-10 border-2 border-slate-300 rounded px-2" value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })}>
            <option value="">Asset…</option>{assets.map((a) => <option key={a.id} value={a.id}>{a.asset_number} · {a.name}</option>)}
          </select>
          <select data-testid="job-type" className="h-10 border-2 border-slate-300 rounded px-2" value={form.maintenance_type_id} onChange={(e) => setForm({ ...form, maintenance_type_id: e.target.value })}>
            <option value="">Maintenance type…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {schedules.length > 0 && (
            <select data-testid="job-schedule" className="h-10 border-2 border-slate-300 rounded px-2" value={form.schedule_id} onChange={(e) => setForm({ ...form, schedule_id: e.target.value })}>
              <option value="">Link a schedule (optional)…</option>{schedules.map((s) => <option key={s.id} value={s.id}>{s.name || s.trigger_unit} — next {s.next_due_date || s.next_due_value || "?"}</option>)}
            </select>
          )}
          <div><label className="text-xs font-semibold text-slate-600">Due date</label><Input data-testid="job-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          <Button data-testid="job-create" onClick={create} disabled={!form.asset_id} className="bg-blue-600 hover:bg-blue-700 font-semibold">Create Job</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
